import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';

export default function UkiesStraplineTitle({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent, forms = [] }) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [bodySourceCodeMode, setBodySourceCodeMode] = useState(false);

    const bodyEditorRef = useRef(null);

    // Get current values with defaults
    const referenceId = componentSettings.reference_id || '';
    const strapline = componentSettings.strapline || '';
    const title = componentSettings.title || '';
    const body = componentSettings.body || '';
    const backgroundColor = componentSettings.background_color || 'white';
    const textAlignment = componentSettings.text_alignment || 'left';
    const paddingTop = componentSettings.padding_top !== undefined ? componentSettings.padding_top : true;
    const paddingBottom = componentSettings.padding_bottom !== undefined ? componentSettings.padding_bottom : true;
    const buttonLabel = componentSettings.button_label || '';
    const buttonLink = componentSettings.button_link || '';
    const buttonColor = componentSettings.button_color || 'default';

    // Parse HTML content to extract configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            
            const fullwidthDiv = doc.querySelector('.structure.structure-fullwidth.ukies-strapline-title');
            if (!fullwidthDiv) return null;
            
            const innerDiv = fullwidthDiv.querySelector('.structure.structure-1');
            if (!innerDiv) return null;
            
            const config = {
                reference_id: fullwidthDiv.getAttribute('id') || '',
                strapline: '',
                title: '',
                body: '',
                background_color: 'white',
                text_alignment: 'left',
                padding_top: true,
                padding_bottom: true,
                button_label: '',
                button_link: '',
                button_color: 'default'
            };

            // Extract background color from style attribute on the fullwidth div
            const bgStyle = fullwidthDiv.getAttribute('style');
            if (bgStyle) {
                const bgColorMatch = bgStyle.match(/background-color:\s*([^;]+)/);
                if (bgColorMatch) {
                    const bgColorValue = bgColorMatch[1].trim();
                    if (bgColorValue === '#ffffff' || bgColorValue === 'white') {
                        config.background_color = 'white';
                    } else if (bgColorValue.includes('--ukies-superlight-green') || bgColorValue.includes('#e6ebea')) {
                        config.background_color = 'light-green';
                    }
                }
            }

            // Extract padding from style attribute on the inner div
            const innerStyle = innerDiv.getAttribute('style');
            if (innerStyle) {
                if (innerStyle.includes('padding-top: 4rem')) {
                    config.padding_top = true;
                } else if (innerStyle.includes('padding-top: 0rem')) {
                    config.padding_top = false;
                }
                if (innerStyle.includes('padding-bottom: 4rem')) {
                    config.padding_bottom = true;
                } else if (innerStyle.includes('padding-bottom: 0rem')) {
                    config.padding_bottom = false;
                }
            }

            // Extract text content and alignment
            const textComponent = doc.querySelector('.component-text');
            if (textComponent) {
                const straplineEl = textComponent.querySelector('.preheader-text');
                const titleEl = textComponent.querySelector('.header-text');
                const bodyEl = textComponent.querySelector('.body-text');
                
                config.strapline = straplineEl ? straplineEl.textContent.trim() : '';
                config.title = titleEl ? titleEl.textContent.trim() : '';
                config.body = bodyEl ? bodyEl.innerHTML.trim() : '';
                
                // Check for center alignment
                if (textComponent.classList.contains('center')) {
                    config.text_alignment = 'center';
                }
            }

            // Extract button information
            const buttonComponent = doc.querySelector('.component-button');
            if (buttonComponent) {
                const buttonEl = buttonComponent.querySelector('button');
                const linkEl = buttonComponent.querySelector('a');
                
                if (buttonEl) {
                    config.button_label = buttonEl.textContent.trim();
                    config.button_color = buttonEl.classList.contains('highlight') ? 'highlight' : 'default';
                }
                
                if (linkEl) {
                    config.button_link = linkEl.getAttribute('href') || '';
                }

                // Check if button is centered by looking at the style attribute
                const buttonStyle = buttonComponent.getAttribute('style');
                if (buttonStyle && buttonStyle.includes('justify-content: center')) {
                    config.text_alignment = 'center';
                }
            }

            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // HTML escaping function
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Generate HTML content from current form values
    const generateHTMLContent = () => {
        return generateHTMLContentWithSettings(componentSettings);
    };

    // Generate HTML content with specific settings
    const generateHTMLContentWithSettings = (settings) => {
        const refId = settings.reference_id || referenceId;
        const straplineText = settings.strapline || strapline;
        const titleText = settings.title || title;
        const bodyText = settings.body || body;
        const backgroundColorValue = settings.background_color || backgroundColor;
        const textAlignmentValue = settings.text_alignment || textAlignment;
        const buttonLabelValue = settings.button_label || buttonLabel;
        const buttonLinkValue = settings.button_link || buttonLink;
        const buttonColorValue = settings.button_color || buttonColor;

        // Generate fullwidth div style (always includes max-width: unset)
        const baseStyle = 'max-width: unset';
        const backgroundColorStyle = backgroundColorValue === 'light-green' 
            ? 'background-color: var(--ukies-superlight-green)'
            : backgroundColorValue === 'white' ? 'background-color: #ffffff' : '';
        
        const fullwidthStyle = backgroundColorStyle 
            ? ` style="${baseStyle}; ${backgroundColorStyle};"`
            : ` style="${baseStyle};"`;

        // Generate padding styles for inner div
        const paddingTopValue = settings.padding_top !== undefined ? settings.padding_top : paddingTop;
        const paddingBottomValue = settings.padding_bottom !== undefined ? settings.padding_bottom : paddingBottom;
        const paddingTopStyle = `padding-top: ${paddingTopValue ? '4rem' : '0rem'}`;
        const paddingBottomStyle = `padding-bottom: ${paddingBottomValue ? '4rem' : '0rem'}`;
        const innerPaddingStyle = ` style="${paddingTopStyle}; ${paddingBottomStyle}"`;

        // Generate text alignment class
        const textAlignmentClass = textAlignmentValue === 'center' ? ' center' : '';

        // Generate button HTML - only if there's a label
        let buttonHTML = '';
        if (buttonLabelValue && buttonLabelValue.trim()) {
            const buttonClass = buttonColorValue === 'highlight' ? ' class="highlight"' : '';
            const buttonStyle = textAlignmentValue === 'center' ? ' style="justify-content: center"' : '';
            buttonHTML = `        <div class="component component-button"${buttonStyle}>
            <a href="${escapeHtml(buttonLinkValue)}">
                <button${buttonClass} type="button">${escapeHtml(buttonLabelValue)}</button>
            </a>
        </div>`;
        }

        // Generate classes for the outer div
        const outerClasses = `structure structure-fullwidth pt-0 pb-0 ukies-strapline-title${refId ? ' anchor' : ''}`;
        
        const htmlContent = `<div class="${outerClasses}"${refId ? ` id="${escapeHtml(refId)}"` : ''}${fullwidthStyle}>
    <div class="structure structure-1 gap-0"${innerPaddingStyle}>
        <div class="component component-text${textAlignmentClass}">
            <div class="preheader-text">${escapeHtml(straplineText)}</div>
            <div class="header-text">${escapeHtml(titleText)}</div>
            <div class="body-text">${bodyText}</div>
        </div>
${buttonHTML}
    </div>
</div>`;

        return htmlContent;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                setComponentSettings(prev => ({
                    ...prev,
                    ...parsed
                }));
            }
            setIsInitialized(true);
        }
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
    }, [selectedComponent?.id]);

    // Set initial content for editor when data changes
    useEffect(() => {
        if (bodyEditorRef.current && body !== undefined) {
            const isFocused = document.activeElement === bodyEditorRef.current;
            
            if (!isFocused) {
                if (bodySourceCodeMode) {
                    bodyEditorRef.current.value = body || '';
                } else {
                    bodyEditorRef.current.innerHTML = body || '<p>Voer hier de tekst in...</p>';
                }
            }
        }
    }, [body, bodySourceCodeMode]);

    // Update HTML content when settings change
    const updateHTMLContent = () => {
        const newContent = generateHTMLContent();
        
        setComponentSettings(prev => ({
            ...prev,
            content: newContent
        }));
        
        onChange('content', newContent);
    };

    // Update HTML content with specific settings (for immediate updates)
    const updateHTMLContentWithSettings = (settings) => {
        const newContent = generateHTMLContentWithSettings(settings);
        
        setComponentSettings(prev => ({
            ...prev,
            content: newContent
        }));
        
        onChange('content', newContent);
    };

    // Handle field changes
    const handleFieldChange = (key, value) => {
        const newComponentSettings = {
            ...componentSettings,
            [key]: value
        };
        
        // Generate content immediately for instant visual updates
        const newContent = generateHTMLContentWithSettings(newComponentSettings);
        
        setComponentSettings(prev => ({
            ...prev,
            ...newComponentSettings,
            content: newContent
        }));
        
        // Trigger onChange immediately
        onChange(key, value);
        onChange('content', newContent);
    };

    // Text formatting functions for WYSIWYG
    const formatBodyText = (command, value = null) => {
        document.execCommand(command, false, value);
        bodyEditorRef.current.focus();
    };

    const handleBodyTextChange = (e) => {
        const content = e.target.innerHTML;
        if (content === '<p>Voer hier de tekst in...</p>') {
            handleFieldChange('body', '');
        } else {
            handleFieldChange('body', content);
        }
    };

    const handleBodyEditorClick = () => {
        if (bodyEditorRef.current && bodyEditorRef.current.innerHTML === '<p>Voer hier de tekst in...</p>') {
            bodyEditorRef.current.innerHTML = '<p><br></p>';
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(bodyEditorRef.current.firstChild, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    const handleBodySourceCodeToggle = () => {
        if (bodySourceCodeMode) {
            const sourceContent = bodyEditorRef.current.value;
            handleFieldChange('body', sourceContent);
        } else {
            const wysiwygContent = bodyEditorRef.current.innerHTML;
            handleFieldChange('body', wysiwygContent);
        }
        setBodySourceCodeMode(!bodySourceCodeMode);
    };



    return (
        <div>
            <style>{`
                .rich-text-editor {
                    font-family: inherit;
                    font-size: 0.9rem;
                    line-height: 1.5;
                }
                .rich-text-editor:focus {
                    border-color: #80bdff !important;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
                }
                .rich-text-editor p {
                    margin-bottom: 0.5rem;
                }
                .rich-text-editor ul, .rich-text-editor ol {
                    margin-bottom: 0.5rem;
                    padding-left: 1.5rem;
                }
                .rich-text-editor a {
                    color: #007bff;
                    text-decoration: underline;
                }
            `}</style>
            
            <Row className="mb-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Referentie-ID</Form.Label>
                        <Form.Control
                            type="text"
                            value={referenceId}
                            onChange={(e) => handleFieldChange('reference_id', e.target.value)}
                            placeholder="Voer referentie-ID in (optioneel)"
                        />
                        <Form.Text className="text-muted">
                            Gebruik dit als je vanuit ergens anders naar dit blok wilt linken. Voeg #jouw-referentie-ID toe aan het einde van de link.
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Achtergrondkleur</Form.Label>
                        <Form.Select
                            value={backgroundColor}
                            onChange={(e) => handleFieldChange('background_color', e.target.value)}
                        >
                            <option value="white">Wit</option>
                            <option value="light-green">Lichtgroen</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Tekstuitlijning</Form.Label>
                        <Form.Select
                            value={textAlignment}
                            onChange={(e) => handleFieldChange('text_alignment', e.target.value)}
                        >
                            <option value="left">Links</option>
                            <option value="center">Midden</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            
            {/* Padding Options */}
            <div className="mb-3">
                <Form.Label>Padding</Form.Label>
                <div className="d-flex gap-3">
                    <Form.Check
                        type="switch"
                        id="strapline-padding-top-switch"
                        label="Bovenkant"
                        checked={Boolean(componentSettings.padding_top)}
                        onChange={(e) => handleFieldChange('padding_top', e.target.checked)}
                    />
                    <Form.Check
                        type="switch"
                        id="strapline-padding-bottom-switch"
                        label="Onderkant"
                        checked={Boolean(componentSettings.padding_bottom)}
                        onChange={(e) => handleFieldChange('padding_bottom', e.target.checked)}
                        style={{marginLeft:'1.5rem'}}
                    />
                </div>
            </div>

            <Form.Group className="mb-3">
                <Form.Label>Strapline</Form.Label>
                <Form.Control
                    type="text"
                    value={strapline}
                    onChange={(e) => handleFieldChange('strapline', e.target.value)}
                    placeholder="Voer strapline in"
                />
            </Form.Group>
            
            <Form.Group className="mb-3">
                <Form.Label>Titel</Form.Label>
                <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Voer titel in"
                />
            </Form.Group>
            
            <Form.Group className="mb-3">
                <Form.Label>Tekst</Form.Label>
                
                {/* Rich Text Editor Toolbar */}
                <div className="border rounded-top p-2 bg-light">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatBodyText('bold')}
                        disabled={bodySourceCodeMode}
                    >
                        <strong>B</strong>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatBodyText('italic')}
                        disabled={bodySourceCodeMode}
                    >
                        <em>I</em>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatBodyText('underline')}
                        disabled={bodySourceCodeMode}
                    >
                        <u>U</u>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatBodyText('insertUnorderedList')}
                        disabled={bodySourceCodeMode}
                    >
                        • List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatBodyText('insertOrderedList')}
                        disabled={bodySourceCodeMode}
                    >
                        1. List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) formatBodyText('createLink', url);
                        }}
                        disabled={bodySourceCodeMode}
                    >
                        🔗 Link
                    </Button>

                    <Button
                        variant={bodySourceCodeMode ? "primary" : "outline-secondary"}
                        size="sm"
                        className="ms-auto"
                        onClick={handleBodySourceCodeToggle}
                    >
                        {bodySourceCodeMode ? "WYSIWYG" : "</>"}
                    </Button>
                </div>
                
                {/* Editor Area */}
                {bodySourceCodeMode ? (
                    <Form.Control
                        ref={bodyEditorRef}
                        as="textarea"
                        rows={6}
                        value={body || ''}
                        onChange={(e) => handleFieldChange('body', e.target.value)}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            borderTop: 'none',
                            borderTopLeftRadius: '0',
                            borderTopRightRadius: '0'
                        }}
                        placeholder="Voer hier de HTML-code in..."
                    />
                ) : (
                    <div
                        ref={bodyEditorRef}
                        contentEditable="true"
                        onInput={handleBodyTextChange}
                        onClick={handleBodyEditorClick}
                        className="rich-text-editor"
                        style={{
                            minHeight: '150px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #ced4da',
                            borderTop: 'none',
                            borderBottomLeftRadius: '0.25rem',
                            borderBottomRightRadius: '0.25rem',
                            padding: '0.75rem',
                            backgroundColor: 'white',
                            outline: 'none'
                        }}
                        suppressContentEditableWarning={true}
                    />
                )}
            </Form.Group>

            {/* Button Section */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Knop (optioneel)
            </h6>
            
            <Row className="mb-3">
                <Col md={8}>
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            type="text"
                            value={buttonLabel}
                            onChange={(e) => handleFieldChange('button_label', e.target.value)}
                            placeholder="Voer knop tekst in"
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Kleur</Form.Label>
                        <Form.Select
                            value={buttonColor}
                            onChange={(e) => handleFieldChange('button_color', e.target.value)}
                        >
                            <option value="default">Groen</option>
                            <option value="highlight">Oranje</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            
            <Form.Group className="mb-3">
                <Form.Label>Link</Form.Label>
                <Form.Control
                    type="text"
                    value={buttonLink}
                    onChange={(e) => handleFieldChange('button_link', e.target.value)}
                    placeholder="Voer link in"
                />
            </Form.Group>


        </div>
    );
}